/* eslint-disable @typescript-eslint/no-magic-numbers */
import React, { RefObject, useMemo } from 'react';
import { useMediaQuery } from '@mui/material';
import { CloseIcon } from '../../icons';
import { MOBILE_BREAKPOINT_THRESHOLD, styled } from '../../theme';
import { Typography } from '../typography';
import { Button } from '../button';
import { CloseThinIcon } from '../../icons/closeThin';
import { useTheme } from '../../hooks/useTheme';
import { Box } from '../box';
import { mergeSx } from '../../utils/sx/merge';
import { ChevronRightIcon } from '../../icons/chevronRight';
import { Stack } from '../stack';
import { SVGIconTokens } from '../svgIcon';
import { AppModalAction, ModalActions } from './components/actions';
import { Puller } from './components/puller';
import { MODAL_LAYOUT_HORIZONTAL_SPACING_DESKTOP, MODAL_MOBILE_BREAKPOINT_THRESHOLD, ModalLayoutVariant, Tokens, name } from './const';
import { ModalLayoutProps } from './model';
import { useModalLayoutContext } from './context';
import { HeaderIcon } from './components/headerIcon';
import { ModalContainer } from './container';
import { ModalHeader } from './header';

const FullscreenRoot = styled('div', { name })(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  height: 'max-content',
  flexGrow: 1,
  paddingTop: Tokens.PaddingTop(theme.spacing(2.5)),
  paddingBottom: Tokens.PaddingBottom(theme.spacing(2.5)),
}));

const PrimaryRoot = styled('div', { name })(({ theme }) => ({
  [Tokens.PullerColor]: theme.palette['gray light'],
  [Tokens.HeaderIconTop]: '-2.8rem',
  position: 'relative',
  maxHeight: '100%',
  flexGrow: 1,
  paddingTop: Tokens.PaddingTop(),
  display: 'flex',
  flexDirection: 'column',
  paddingBottom: Tokens.PaddingBottom(theme.spacing(2.5)),

  [theme.breakpoints.up(MODAL_MOBILE_BREAKPOINT_THRESHOLD)]: {
    paddingTop: Tokens.PaddingTop(theme.spacing(7.25)),
    [Tokens.HeaderIconTop]: '-3rem',
    paddingInline: Tokens.PaddingX(theme.spacing(MODAL_LAYOUT_HORIZONTAL_SPACING_DESKTOP)),
    paddingBottom: Tokens.PaddingBottom(theme.spacing(7.25)),
  },
}));

const RightRoot = styled('div', { name })(({ theme }) => ({
  [Tokens.PullerColor]: theme.palette['gray light'],
  [Tokens.HeaderIconTop]: '-2.8rem',
  position: 'relative',
  maxHeight: '100%',
  backgroundColor: theme.palette.background.paper,
  paddingTop: Tokens.PaddingTop(),
  display: 'flex',
  flexDirection: 'column',
  paddingBottom: Tokens.PaddingBottom(theme.spacing(2.5)),

  [theme.breakpoints.up(MODAL_MOBILE_BREAKPOINT_THRESHOLD)]: {
    [Tokens.HeaderIconTop]: '-3rem',
    [Tokens.PaddingTop]: theme.spacing(2),
    paddingLeft: theme.spacing(MODAL_LAYOUT_HORIZONTAL_SPACING_DESKTOP),
    paddingRight: theme.spacing(MODAL_LAYOUT_HORIZONTAL_SPACING_DESKTOP),
    paddingBottom: Tokens.PaddingBottom(theme.spacing(7.25)),
  },
}));

export const ModalLayout = React.forwardRef<HTMLDivElement, React.PropsWithChildren<ModalLayoutProps>>(
  (
    {
      title,
      icon,
      titleProps: _titleProps,
      footer,
      swipable = true,
      isMobile: isMobileFromProps,
      containerSx,
      message,
      actions,
      onClose,
      onBack,
      children,
      id,
      swipeAreaRef: swipableAreaRef,
      variant = ModalLayoutVariant.primary,
    },
    ref,
  ) => {
    const theme = useTheme();
    const isMobileFromMediaQuery = useMediaQuery(theme.breakpoints.down(MODAL_MOBILE_BREAKPOINT_THRESHOLD));
    const { isFullscreen } = useModalLayoutContext();
    const { sx: titlePropsSx, ...titleProps } = _titleProps ?? {};
    const Root = useMemo(() => {
      switch (variant) {
        case ModalLayoutVariant.drawerRight: {
          return RightRoot;
        }
        default: {
          return PrimaryRoot;
        }
      }
    }, [variant]);

    const isMobile = isMobileFromProps ?? isMobileFromMediaQuery;

    if (isFullscreen) {
      return (
        <FullscreenRoot
          id={id}
          ref={ref}
          role="presentation"
          sx={mergeSx(
            {
              pt: Boolean(onBack) && isMobile ? 0 : 2.5,
            },
            containerSx,
          )}
        >
          <ModalContainer>
            {Boolean(title || onBack) && (
              <ModalHeader component="div" sx={titlePropsSx} {...titleProps}>
                {Boolean(onBack) && isMobile ? (
                  <Stack
                    direction="row"
                    sx={{
                      typography: 'h2',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: '17px 12px',
                      position: 'relative',
                    }}
                  >
                    <Button
                      onClick={onBack}
                      sx={{
                        position: 'absolute',
                        zIndex: 10,
                        left: 0,
                        transform: 'rotate(180deg)',
                        [SVGIconTokens.Size]: '1.75rem',
                      }}
                      variant="text"
                    >
                      <ChevronRightIcon />
                    </Button>
                    {Boolean(title) && <div>{title}</div>}
                  </Stack>
                ) : (
                  title
                )}
              </ModalHeader>
            )}
          </ModalContainer>
          {Boolean(onClose) && (
            <Button
              color="white"
              onClick={onClose}
              size="small"
              square
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                backgroundColor: { xs: 'transparent !important', [MOBILE_BREAKPOINT_THRESHOLD]: undefined },
                [SVGIconTokens.Size]: { xs: '2rem', [MODAL_MOBILE_BREAKPOINT_THRESHOLD]: '1.75rem' },
              }}
              variant="primary"
            >
              {isMobile ? <CloseIcon /> : <CloseThinIcon />}
            </Button>
          )}
          {children}
        </FullscreenRoot>
      );
    }

    return (
      <Root
        id={id}
        ref={ref}
        role="presentation"
        sx={mergeSx(
          {
            ...(icon && { [Tokens.PaddingTop]: theme.spacing(isMobile ? '50px' : 10) }),
          },
          containerSx,
        )}
      >
        {swipable && isMobile && !icon && <Puller ref={swipableAreaRef as RefObject<HTMLDivElement>} />}
        {Boolean(onClose || onBack) && !isMobile && (
          <Button
            color="white"
            onClick={onClose || onBack}
            size="small"
            square
            sx={{
              position: 'absolute',
              right: theme.spacing(1),
              top: theme.spacing(variant === ModalLayoutVariant.drawerRight ? 1.75 : 1),
              background: variant === ModalLayoutVariant.drawerRight ? 'transparent' : undefined,
            }}
            variant="primary"
          >
            <CloseThinIcon sx={{ fontSize: '2rem' }} />
          </Button>
        )}
        {Boolean(title || onClose || onBack || icon) && (
          <ModalContainer sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start' }}>
            {icon && <HeaderIcon>{icon}</HeaderIcon>}
            {Boolean(title) && (
              <Typography
                sx={{
                  textAlign: variant === ModalLayoutVariant.drawerRight ? 'left' : 'center',
                  flexGrow: 1,
                  overflow: 'hidden',
                  pb: isMobile ? undefined : 3.5,
                  pr: isMobile ? 1 : undefined,
                  py: isMobile ? 2 : undefined,
                  mt: icon ? 2 : 0,
                  ml: variant === ModalLayoutVariant.drawerRight ? '-2.5px' : undefined,
                  ...titlePropsSx,
                }}
                variant={variant === ModalLayoutVariant.drawerRight ? 'h2' : isMobile ? 'h5' : 'h4'}
                {...titleProps}
              >
                {title}
              </Typography>
            )}
          </ModalContainer>
        )}

        {Boolean(message) && <Typography variant="body">{message}</Typography>}
          {children}

        {Boolean(actions) && <ModalActions actions={actions as AppModalAction[]} />}

        {footer}
      </Root>
    );
  },
);
ModalLayout.displayName = 'ModalLayout';
